import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { formatISO } from "date-fns";
import { useToast } from "./Toast";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

interface ProjectCommandSidebarProps {
  projectId: string;
  onPromptResponse: (output: string) => void;
  onLoadingStateChange: (isLoading: boolean) => void;
}

interface Message {
  id: string;
  sender: "user" | "assistant";
  content: string;
  status?: "sending" | "error";
}

const ProjectCommandSidebar: React.FC<ProjectCommandSidebarProps> = ({
  projectId,
  onPromptResponse,
  onLoadingStateChange,
}) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "initial-message",
      sender: "assistant",
      content: "Ask me to update your project.",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { showToast } = useToast();

  const sendMessageToAPI = async (
    messageContent: string,
    messageId: string
  ) => {
    const message = messages.find((msg) => msg.id === messageId) || {
      id: messageId,
      sender: "user",
      content: messageContent,
      status: "sending",
    };

    // Update message status to sending
    setMessages([{ ...message, status: "sending" }]);
    setIsLoading(true);
    onLoadingStateChange(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/projects/${projectId}/prompt`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentTimestamp: formatISO(new Date()),
            prompt: messageContent,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get response from Echo");
      }

      const data: { output: string; response: string } = await response.json();
      onPromptResponse(data.output);

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        sender: "assistant",
        content: data.response || "I processed your request.",
      };

      // Update the user message to remove the sending status
      const updatedUserMessage = { ...message, status: undefined };
      setMessages([updatedUserMessage, assistantMessage]);

      return { success: true };
    } catch (error) {
      console.error("Error sending message:", error);
      showToast("Failed to get a response from Echo. Try again.", "red");

      const updatedUserMessage = { ...message, status: "error" as const };
      setMessages([updatedUserMessage]);

      return { success: false, error };
    } finally {
      setIsLoading(false);
      onLoadingStateChange(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputValue.trim()) return;

    const messageId = `user-${Date.now()}`;
    const messageContent = inputValue;

    // Clear input immediately for better UX
    setInputValue("");

    await sendMessageToAPI(messageContent, messageId);
  };

  const handleRetry = async (messageId: string) => {
    const messageToRetry = messages.find((msg) => msg.id === messageId);
    if (!messageToRetry) return;

    await sendMessageToAPI(messageToRetry.content, messageId);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-126px)]">
      <div className="flex flex-col flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-4 py-2 ${
                message.sender === "user"
                  ? "bg-gray-200 text-gray-900"
                  : "bg-white text-gray-900"
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              {message.status === "error" && (
                <div className="mt-1 flex items-center">
                  <span className="text-xs text-red-300">Failed to send</span>
                  <button
                    onClick={() => handleRetry(message.id)}
                    className="ml-2 text-xs underline text-white hover:text-gray-200"
                    disabled={isLoading}
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Animated ellipses for loading state */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-lg px-4 py-2 bg-gray-100 text-gray-900">
              <div className="flex items-center space-x-1">
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0ms" }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "150ms" }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "300ms" }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1" />
      </div>

      <div className="flex p-4 border-t border-gray-200 w-full">
        <form onSubmit={handleSubmit} className="flex items-center w-full">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask Echo to update your project..."
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
            rows={1}
            disabled={isLoading}
          />
          <button
            type="submit"
            className="ml-2 inline-flex justify-center rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:bg-blue-300"
            disabled={!inputValue.trim() || isLoading}
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProjectCommandSidebar;
